/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface ConfirmActionDialogData {
  title: string;
  text: string;
  confirmLabel: string;
  destructive?: boolean;
}

// Design-system confirmation dialog for feed card actions. Closes with `true` when confirmed.
@Component({
  selector: 'ut-confirm-action-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  template: `
    <div class="confirm-dialog">
      <h2 class="confirm-dialog__title" mat-dialog-title>{{ data.title }}</h2>
      <p class="confirm-dialog__text">{{ data.text }}</p>
      <div class="confirm-dialog__actions">
        <button type="button" class="ut-btn ut-btn--secondary" [mat-dialog-close]="false" cdkFocusInitial>Cancel</button>
        <button type="button" class="ut-btn" [class.confirm-dialog__destructive]="data.destructive" [mat-dialog-close]="true">{{ data.confirmLabel }}</button>
      </div>
    </div>
  `,
  styles: [`
    /* Fills the panel: ng-falcon's DialogService gives every dialog a 500px minimum width, so a fixed narrower
       width left the content off-centre. */
    .confirm-dialog {
      box-sizing: border-box;
      width: 100%;
      padding: var(--spacing-3xl);
      font-family: var(--font-body);
    }

    /* mat-dialog-title brings Material's own font and letter-spacing. */
    .confirm-dialog__title {
      margin: 0;
      padding: 0;
      font-family: var(--font-body);
      letter-spacing: normal;
      color: var(--text-primary);
      font-size: var(--text-lg);
      line-height: var(--text-lg-lh);
      font-weight: 600;
    }

    .confirm-dialog__title::before {
      display: none;
    }

    .confirm-dialog__text {
      margin: var(--spacing-xs) 0 0;
      color: var(--text-tertiary);
      font-size: var(--text-sm);
      line-height: var(--text-sm-lh);
    }

    .confirm-dialog__actions {
      display: flex;
      gap: var(--spacing-lg);
      margin-top: var(--spacing-4xl);
    }

    .confirm-dialog__actions .ut-btn {
      flex: 1;
    }

    .confirm-dialog__destructive {
      background-color: var(--bg-error-solid);
    }

    .confirm-dialog__destructive:hover,
    .confirm-dialog__destructive:focus {
      background-color: var(--bg-error-solid-hover);
    }
  `],
})
export class ConfirmActionDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: ConfirmActionDialogData) {}
}
